import { useCallback, useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'sense-ui-theme';
const DARK_CLASS_NAME = 'dark';
const DARK_MODE_ENABLED = process.env.NODE_ENV !== 'production';

function getPreferredTheme() {
  if (!DARK_MODE_ENABLED) {
    return 'light';
  }
  if (typeof window === 'undefined') {
    return 'light';
  }

  let stored;
  try {
    stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch (error) {
    stored = null;
  }

  if (stored === 'dark' || stored === 'light') {
    return stored;
  }

  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  return prefersDark ? 'dark' : 'light';
}

function applyThemeToDocument(theme) {
  if (typeof document === 'undefined') {
    return;
  }

  const effectiveTheme = DARK_MODE_ENABLED ? theme : 'light';
  document.documentElement.classList.toggle(DARK_CLASS_NAME, effectiveTheme === 'dark');
  document.documentElement.dataset.theme = effectiveTheme;
}

export function useDarkMode() {
  const [theme, setTheme] = useState(() => getPreferredTheme());

  useEffect(() => {
    const effectiveTheme = DARK_MODE_ENABLED ? theme : 'light';
    applyThemeToDocument(effectiveTheme);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, effectiveTheme);
      } catch (error) {
        // Swallow write errors (e.g. Safari private mode).
      }
    }
  }, [theme]);

  useEffect(() => {
    if (!DARK_MODE_ENABLED || typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY && event.newValue) {
        setTheme(event.newValue === 'dark' ? 'dark' : 'light');
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const toggleTheme = useCallback(
    () => {
      if (!DARK_MODE_ENABLED) {
        return;
      }
      setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
    },
    []
  );

  const setThemeExplicit = useCallback(
    (nextTheme) => setTheme(DARK_MODE_ENABLED && nextTheme === 'dark' ? 'dark' : 'light'),
    []
  );

  const effectiveTheme = DARK_MODE_ENABLED ? theme : 'light';
  return {
    theme: effectiveTheme,
    isDark: effectiveTheme === 'dark',
    toggleTheme,
    setTheme: setThemeExplicit,
  };
}

export function initializeThemeFromStorage() {
  const theme = getPreferredTheme();
  applyThemeToDocument(theme);
  return theme;
}
